import React, { FunctionComponent, useState } from 'react'
import RenderImage from '../../entities/RenderImage'
import { getLoadingClassName } from '../loading-indicator-wrapper'
import GalleryModal from '../gallery-modal'
import EnlargeIcon from '../../assets/media/icons/enlarge.svg'
import style from './renderImagesPreview.css'
import RenderImagePlaceholder from '../../assets/media/car-silhouette41.svg'
import { normalizeUrl } from '../../helpers/normalizeString'
import useE3DAvailability from '../../hooks/useE3DAvailability'
import E3dStream from '../e3d-components/e3d-stream/e3DStreamContainer'
import { useSelector } from 'react-redux'
import is3DExhibitionEnabledSelector from '../../redux/selectors/feature-scope/is3DExhibitionEnabledSelector'

const pickRenderDetailBySize = (renderDetails: RenderImage['renderDetails'] | undefined, size: number) =>
    renderDetails?.find(renderDetail => renderDetail.size === size)
    ?? renderDetails?.find(renderDetail => renderDetail.size > size)

export type Props = {
    renderImages: RenderImage[] | null
    activeRenderImgIdx: number
    setActiveRenderImgIdx: Function
    isLoading: boolean
    isHidden: boolean
}

const RenderImagesPreview: FunctionComponent<Props> = (props) => {
    const {
        renderImages,
        activeRenderImgIdx,
        setActiveRenderImgIdx,
        isLoading,
        isHidden,
    } = props

    const modalActiveState = useState(false)
    const setModalIsActive = modalActiveState[1]

    const isE3DAvailable = useE3DAvailability(true)
    const isE3DEnabled = useSelector(is3DExhibitionEnabledSelector)
    const [isE3DStreamActive, setIsE3DStreamActive] = useState(true)

    if (isHidden || renderImages == null) {
        return null
    }

    if (isE3DAvailable && isE3DEnabled && isE3DStreamActive) {
        return (
            <E3dStream setIsE3DStreamActive={setIsE3DStreamActive}/>
        )
    }

    const activeImg = renderImages[activeRenderImgIdx]
    const showPlaceholder = (
        renderImages.length === 0
        || activeRenderImgIdx == null
        || pickRenderDetailBySize(activeImg?.renderDetails, 400)?.url === undefined
    )

    const img = showPlaceholder
        ? <RenderImagePlaceholder className={style.placeholderImg}/>
        : <img src={pickRenderDetailBySize(activeImg?.renderDetails, 400).url} className={style.img} alt="" width={400} height={240} data-testid="cart-gallery"/>

    const thumbnailElements = renderImages.map((image, index) => {
        const onMouseEnter = (): void => {
            setActiveRenderImgIdx(index)
        }

        return (
            <li
                key={image.id}
                className={style.thumbnail}
                onMouseEnter={onMouseEnter}
            />
        )
    })

    const openModal = (): void => {
        setModalIsActive(true)
    }

    const images = renderImages.map((renderImage) => ({
        src: pickRenderDetailBySize(renderImage.renderDetails, 900)?.url,
        width: 900,
        height: 540,
    }))
    const thumbnails = renderImages.map((renderImage) => ({
        src: pickRenderDetailBySize(renderImage.renderDetails, 100)?.url,
        width: 100,
        height: 60,
    }))
    const miwiImagesUrls = renderImages
        .map((renderImage) => {
            const details = renderImage.renderDetails ?? []
            const largestImage = details.reduce(
                (biggest, current) => (current.size > biggest.size ? current : biggest),
                details[0],
            )
            return (pickRenderDetailBySize(details, 3840) ?? largestImage)?.url
        })
        .map(imageUrl => normalizeUrl(imageUrl))

    return (
        <>
            <div className={`${style.container} ${getLoadingClassName(isLoading)}`}>
                <button onClick={openModal} className={style.button} type="button" data-testid="cart-gallery-enlarge">
                    <EnlargeIcon/>
                </button>
                {img}
                <ul className={style.thumbnailList}>
                    {thumbnailElements}
                </ul>
            </div>
            <GalleryModal
                modalActiveState={modalActiveState}
                images={images}
                thumbnails={thumbnails}
                miwiImagesUrls={miwiImagesUrls}
                activeImgIdx={activeRenderImgIdx}
            />
        </>
    )
}

export default RenderImagesPreview
